import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  BrowserAuthError,
} from '@azure/msal-browser';
import { useAuth } from '../stateManagement/auth/useAuth';
import { Loader } from '../components/organisms/Loader';
import { loginRequest } from '../stateManagement/auth/authConfig';
import { appInsights } from '../appInsights';
import { log } from '../utils';

const ALLOWED_DOMAINS = ['helpshift.com', 'keywords.com'];

const isAllowedEmail = (email?: string) => {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return ALLOWED_DOMAINS.includes(domain);
};

// Helper function to check if error requires user interaction
const requiresInteraction = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  // Check for BrowserAuthError with timeout or iframe-related errors
  if (error instanceof BrowserAuthError) {
    const errorCode = (error as any).errorCode || '';
    const errorMessage = error.message || '';
    // These error codes indicate that silent token acquisition failed and user interaction is needed
    const interactionRequiredCodes = [
      'monitor_window_timeout',
      'user_cancelled',
      'popup_window_error',
      'empty_scopes_error',
      'invalid_authority',
    ];

    return interactionRequiredCodes.some(
      code =>
        errorCode.toLowerCase().includes(code.toLowerCase()) ||
        errorMessage.toLowerCase().includes(code.toLowerCase()),
    );
  }

  return false;
};

// const getPragmaId = (token: string | null): string | null => {
//   if (!token) return null;
//   try {
//     const payload = token.split('.')[1];
//     const decodedPayload = JSON.parse(atob(payload));
//     return decodedPayload?.pragmaId ?? null;
//   } catch {
//     return null;
//   }
// };

// Helper function to decode token claims
const decodeTokenClaims = (token: string | null): any => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const decodedPayload = JSON.parse(atob(payload));
    return decodedPayload;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error decoding token:', error);
    return null;
  }
};

// const getAgeGroup = (token: string | null): string | null => {
//   if (!token) return null;

//   try {
//     const payload = token.split('.')[1];
//     const decodedPayload = JSON.parse(atob(payload));

//     return decodedPayload?.ageGroup ?? null;
//   } catch (error) {
//     return null;
//   }
// };

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, accessToken, setAccessToken } = useAuth();
  const { inProgress, instance } = useMsal();
  const [loading, setLoading] = useState(true);
  const isFetchingToken = useRef(false);
  const redirectAttempts = useRef(0); // Track redirect attempts
  const lastFetchAttempt = useRef<number>(0); // Track last fetch attempt time
  const navigate = useNavigate();
  const location = useLocation();
  const MAX_REDIRECT_ATTEMPTS = 2; // Prevent infinite loops (reduced from 3)
  const MIN_FETCH_INTERVAL_MS = 3000; // Minimum time between fetch attempts (3 seconds)

  useEffect(() => {
    const fetchToken = async () => {
      // Prevent concurrent calls
      if (isFetchingToken.current) {
        return;
      }

      // Prevent redirect loops - don't fetch token if already on login page
      if (location.pathname === '/login') {
        setLoading(false);
        return;
      }

      // Rate limiting - prevent too frequent fetch attempts
      const now = Date.now();
      if (now - lastFetchAttempt.current < MIN_FETCH_INTERVAL_MS) {
        return;
      }
      lastFetchAttempt.current = now;

      // Prevent infinite redirect attempts
      if (redirectAttempts.current >= MAX_REDIRECT_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.error('Maximum redirect attempts reached. Redirecting to login with error.');
        navigate('/login?error=Authentication failed. Please try again.', {
          replace: true,
        });
        setLoading(false);
        return;
      }

      isFetchingToken.current = true;
      setLoading(true);

      try {
        const accounts = instance.getAllAccounts();

        if (accounts.length === 0) {
          redirectAttempts.current += 1;
          await instance.loginRedirect(loginRequest);
          return;
        }

        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });

          // Reset redirect attempts on success
          redirectAttempts.current = 0;
          //  Use accessToken instead of idToken
          setAccessToken(tokenResponse.idToken);

          // Log token and all claims for debugging
          // eslint-disable-next-line no-console
          // console.log('=== Token Response ===', tokenResponse);
          log(false, '=== Token Response ===', tokenResponse);

          // eslint-disable-next-line no-console
          // console.log('=== Access Token ===', tokenResponse.accessToken);
          log(false, '=== Access Token ===', tokenResponse.accessToken);
          // eslint-disable-next-line no-console
          // console.log('=== ID Token ===', tokenResponse.idToken);
          log(false, '=== ID Token ===', tokenResponse.idToken);

          // Decode and log access token claims
          const accessTokenClaims = decodeTokenClaims(tokenResponse.accessToken);
          // eslint-disable-next-line no-console
          // console.log('=== Access Token Claims ===', accessTokenClaims);
          log(false, '=== Access Token Claims ===', accessTokenClaims);

          // Decode and log ID token claims
          const idTokenClaims = decodeTokenClaims(tokenResponse.idToken);
          // eslint-disable-next-line no-console
          // console.log('=== ID Token Claims ===', idTokenClaims);
          log(false, '=== ID Token Claims ===', idTokenClaims);

          const email = idTokenClaims?.emails?.[0];

          if (!isAllowedEmail(email)) {
            // eslint-disable-next-line no-console
            console.warn('Unauthorized email:', email);
            navigate('/forbidden', { replace: true });
            // eslint-disable-next-line no-useless-return
            return;
          }
        } catch (error) {
          // Check if error requires interaction (including timeout errors)
          if (requiresInteraction(error)) {
            // eslint-disable-next-line no-console
            console.warn('Token acquisition requires user interaction. Redirecting to login.');
            redirectAttempts.current += 1;

            // Only redirect if we haven't exceeded max attempts
            if (redirectAttempts.current <= MAX_REDIRECT_ATTEMPTS) {
              await instance.loginRedirect(loginRequest);
            } else {
              // Max attempts reached, navigate to login page with error
              navigate('/login?error=Authentication failed. Please try again.', {
                replace: true,
              });
            }
          } else {
            // For non-interaction errors, log and navigate to login
            // eslint-disable-next-line no-console
            console.error('Silent token renewal failed:', error);
            appInsights.trackEvent({
              name: 'fetchTokenFailure',
              properties: {
                accessToken,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                errorCode: error instanceof BrowserAuthError ? (error as any).errorCode : undefined,
                timestamp: Date.now(),
              },
            });

            // Don't increment redirectAttempts for non-interaction errors
            // Just navigate to login to let user try again
            navigate(
              `/login?error=${encodeURIComponent(
                'Token acquisition failed. Please try logging in again.',
              )}`,
              { replace: true },
            );
          }
        }
      } catch (error) {
        // Handle unexpected errors
        redirectAttempts.current += 1;
        // eslint-disable-next-line no-console
        console.error('Unexpected error in fetchToken:', error);
        if (redirectAttempts.current >= MAX_REDIRECT_ATTEMPTS) {
          navigate('/login?error=Authentication error. Please refresh the page.', {
            replace: true,
          });
        }
      } finally {
        isFetchingToken.current = false;
        setLoading(false);
      }
    };

    // Only fetch token when not in progress and not already on login page
    if (inProgress === InteractionStatus.None && location.pathname !== '/login') {
      fetchToken();
    } else if (inProgress !== InteractionStatus.None) {
      // Keep loading state while interaction is in progress
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    inProgress,
    instance,
    setAccessToken,
    navigate,
    location.pathname,
    accessToken,
    isAuthenticated,
  ]);

  if (loading || inProgress !== InteractionStatus.None) {
    return <Loader />;
  }

  if (!isAuthenticated || !accessToken) {
    return <Loader />;
  }

  // Check pragmaId
  // const pragmaId = getPragmaId(accessToken);
  // if (!pragmaId) {
  //   // appInsights.trackEvent({
  //   //   name: 'pragmaIdUnavailable',
  //   //   properties: {
  //   //     accessToken,
  //   //     timestamp: Date.now(),
  //   //   },
  //   // });
  //   navigate('/forbidden');
  //   return null;
  // }

  return children ? (children as React.ReactElement) : <Outlet />;
};
